import { Tag } from '~/interfaces/Tag';
import FilterCloseIcon from '../icons/FilterCloseIcon';
import PlusIcon from '../icons/PlusIcon';
import { Collapsible } from '../ui/collapsible';
import TagFilterItem from './TagFilterItem';
import React from 'react';
import CollapseTagsIcon from '../icons/CollapseTagsIcon';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import TagTooltipIcon from '../icons/TagTooltipIcon';
import ExpandTagsIcon from '../icons/ExpandTagsIcon';

export default function TagFilterMenu({
  availableTagIds,
  isMenuOpen,
  onResetFilters,
  onTagClick,
  setIsMenuOpen,
  tags,
}: {
  availableTagIds: Set<number>;
  isMenuOpen: boolean;
  onResetFilters: () => void;
  onTagClick: (tagId: string) => void;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tags: Tag[];
}) {
  const [isListExpanded, setIsListExpanded] = React.useState(false);

  return (
    <div className="flex items-center justify-start px-1">
      <Collapsible
        open={isMenuOpen}
        onOpenChange={(isOpen) => setIsMenuOpen(isOpen)}
        className="w-full rounded-[20px] border border-taupe-light-20 bg-beige-pale p-2"
      >
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center justify-start">
            <h4 className="text-sm font-bold uppercase text-green-primary-text">Apply filters</h4>
            <button
              onClick={onResetFilters}
              type="button"
              className="px-2 pr-4 text-[13px] lowercase text-brown-dark underline"
            >
              Reset all
            </button>
          </div>

          <button type="button" onClick={() => setIsMenuOpen(!isMenuOpen)} className="pl-4">
            {isMenuOpen ? <FilterCloseIcon /> : <PlusIcon />}
          </button>
        </div>

        {isMenuOpen ? (
          <ul
            className={`${isListExpanded ? 'my-2' : 'mt-2'} border border-b-0 border-l-0 border-r-0 border-dashed border-taupe-light-75`}
          >
            {tags.slice(0, 8).map((tag, index) => (
              <TagFilterItem
                isDisabled={!availableTagIds.has(tag.parentTemplateTagId)}
                isFirstItem={index === 0}
                key={tag.tagTitle}
                onClick={() => onTagClick(tag.parentTemplateTagId.toString())}
                tag={tag}
              />
            ))}
            {isListExpanded ? (
              <>
                {tags.slice(8).map((tag) => (
                  <TagFilterItem
                    isDisabled={!availableTagIds.has(tag.parentTemplateTagId)}
                    isFirstItem={false}
                    key={tag.tagTitle}
                    onClick={() => onTagClick(tag.parentTemplateTagId.toString())}
                    tag={tag}
                  />
                ))}
                <li>
                  <button
                    type="button"
                    className="flex h-full w-full items-center justify-center gap-x-2 pt-2 text-sm font-semibold text-brown"
                    onClick={() => setIsListExpanded(false)}
                  >
                    <span>Show fewer filters</span>
                    <span>
                      <CollapseTagsIcon />
                    </span>
                  </button>
                </li>
              </>
            ) : (
              <li
                key="expandTagsBtn"
                className="relative flex items-center justify-center border border-b-0 border-l-0 border-r-0 border-dashed border-taupe-light-75 px-2"
              >
                <span className="flex h-full w-full items-center justify-between pt-1 blur-sm filter">
                  <span className="flex cursor-pointer items-center justify-start gap-x-2">
                    <Checkbox disabled checked={false} id={tags[9].tagTitle} />
                    <Label
                      htmlFor={tags[9].tagTitle}
                      className="cursor-not-allowed p-0 text-base font-extralight text-brown !opacity-50"
                    >
                      {tags[9].tagTitle}
                    </Label>
                  </span>
                  <span className="relative bottom-[1px] p-2">
                    <TagTooltipIcon />
                  </span>
                </span>
                <button
                  type="button"
                  className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center gap-x-2 pt-2 text-sm font-semibold text-brown"
                  onClick={() => setIsListExpanded(true)}
                >
                  <span>Show more filters</span>
                  <span>
                    <ExpandTagsIcon />
                  </span>
                </button>
              </li>
            )}
          </ul>
        ) : null}
      </Collapsible>
    </div>
  );
}
